import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Row } from 'write-excel-file';

import type { WidgetExportOptions } from 'src/components/dashboard/DashboardPage/components/WidgetFrame/useWidgetExport';
import { getRelativeValue } from 'src/components/dashboard/DashboardPage/helper';
import calculateBarChartValues from 'src/components/followUp/common/ChartBackground/calculateBarChartValues';
import type { AggregateList } from 'src/components/followUp/common/types';

import { mapAggregateListToBarChartData } from './helper';

type UseExportOptionsProps = {
  data?: AggregateList;
  comparisonData?: AggregateList;
} & Pick<WidgetExportOptions, 'widgetName' | 'queryFilters'>;

const useMapRows = () => {
  const { t } = useTranslation('followUp');

  return useCallback(
    (data?: AggregateList) => {
      if (!data) return [];

      const mappedData = mapAggregateListToBarChartData(data);
      const values = calculateBarChartValues({
        bars: mappedData,
        showTotals: true,
      });

      return [
        [
          {
            value: t('statistics.npsDistributionWidget.npsDistribution'),
            fontWeight: 'bold',
          },
          {
            value: t('statistics.npsDistributionWidget.amount'),
            fontWeight: 'bold',
          },
        ],
        ...mappedData.map<Row>((bar) => {
          const stackValue = bar.stacks[0].value;
          const stackRelativeValue = getRelativeValue(
            stackValue ?? 0,
            values.totalValue,
            0,
          );

          return [
            {
              value: bar.label,
            },
            {
              value: stackValue,
            },
            {
              value: `${stackRelativeValue}%`,
              align: 'right',
            },
          ];
        }),
      ] satisfies Row[];
    },
    [t],
  );
};

export const useExportOptions = ({
  data,
  comparisonData,
  widgetName,
  queryFilters,
}: UseExportOptionsProps): WidgetExportOptions => {
  const mapRows = useMapRows();
  const rows = useMemo(() => mapRows(data), [data, mapRows]);
  const comparisonRows = useMemo(
    () => (comparisonData ? mapRows(comparisonData) : undefined),
    [comparisonData, mapRows],
  );

  return useMemo(
    () => ({
      widgetName,
      rows,
      comparisonRows,
      queryFilters,
    }),
    [widgetName, rows, comparisonRows, queryFilters],
  );
};
